// Deriving a whole passage (plan §6, §8 entry point 3): one candidate per
// eligible chord, chosen under a rung ceiling and an ADVENTURE band, applied
// together as one new layer. Deterministic — the same original, rung and
// band give the same layer, byte for byte (pinned) — because a derivation
// the user can re-run and compare has to be a function of its inputs, not a
// dice roll. Where several candidates qualify for a chord, `spread()` (the
// golden-ratio sequence the bass and comping generators use) picks among
// them, so a passage does not tritone-sub every single V.

import { PHI, spread } from '../walkingBass';
import * as formEdit from '../score/formEdit';
import { createReharmLayer, type ReharmDerivation } from '../score/reharm';
import type { Score } from '../score/types';
import { suggest, type SuggestOptions } from './suggest';
import {
  adventureBandOf,
  ADVENTURE_BANDS,
  ADVENTURE_CEILING,
  type AdventureBand,
} from './surprisal';
import type { ReharmCandidate, ReharmContext, ReharmRung } from './types';

export interface DeriveOptions {
  /** Sequence indexes to consider (inclusive); default the whole form. */
  from?: number;
  to?: number;
  /** Highest rung consulted. Default 2. */
  maxRung?: ReharmRung;
  /** Surprisal ceiling: candidates beyond the band are dropped. Default 'inside'. */
  adventure?: AdventureBand;
  /** How much of the passage to touch, 0..1 (default 0.5): the share of
   *  eligible chords that take a candidate, spread evenly. */
  density?: number;
  /** Seed for `spread()` so two derivations from one original differ. */
  variation?: number;
  /** Performance tempo in BPM; default the score's own. Fast tempos cap the
   *  harmonic rhythm a candidate may write (see `paceCeiling`). */
  tempo?: number;
}

/** Turns the integer "Another" count into a gate salt whose fractional part
 *  moves with every step (the gate reads only the fraction, so an integer
 *  salt would never change which chords it touches). Irrational, and apart
 *  from PHI so the shifted gate is not just the same sequence one chord on. */
const GATE_SALT = Math.SQRT2 - 1;
const frac = (x: number) => ((x % 1) + 1) % 1;

/**
 * Felts, in every chapter: "reducing the number of chords in each bar… is
 * particularly desirable if the performance tempo is medium to fast", a line
 * cliché at one chord per bar rather than per beat at speed. The most chords
 * per bar a derivation will WRITE at a tempo — a candidate that keeps the
 * written onsets is never capped, since it adds nothing to play. Unknown
 * tempo: no cap.
 */
export function paceCeiling(tempo: number | undefined): number {
  if (!tempo || !Number.isFinite(tempo)) return Number.POSITIVE_INFINITY;
  if (tempo >= 260) return 1;
  if (tempo >= 200) return 2;
  return Number.POSITIVE_INFINITY;
}

/**
 * One candidate per chosen chord, non-overlapping (a secondary dominant
 * rewrites the chord BEFORE its target, so it is filed under that chord and
 * excludes any other candidate there). Candidates are ranked as `suggest`
 * ranks them, then filtered to the band; the spread picks which chords take
 * one and which of their candidates.
 */
/** The four dials with their defaults filled in — what a derivation actually
 *  ran with, and what the layer records so selecting it shows them back. */
export function resolveDeriveOptions(options: DeriveOptions = {}): ReharmDerivation {
  return {
    maxRung: options.maxRung ?? 2,
    adventure: options.adventure ?? 'inside',
    density: Math.max(0, Math.min(1, options.density ?? 0.5)),
    variation: options.variation ?? 0,
  };
}

export function derive(ctx: ReharmContext, options: DeriveOptions = {}): ReharmCandidate[] {
  const from = Math.max(0, options.from ?? 0);
  const to = Math.min(ctx.sequence.length - 1, options.to ?? ctx.sequence.length - 1);
  const { maxRung, adventure: band, density, variation } = resolveDeriveOptions(options);
  const ceiling = ADVENTURE_CEILING[band];
  const pace = paceCeiling(options.tempo ?? ctx.score.tempo);
  const suggestOptions: SuggestOptions = { maxRung, voiceLeading: false };

  // Every qualifying candidate, filed under the chord it actually replaces.
  const byTarget = new Map<number, ReharmCandidate[]>();
  for (let at = from; at <= to; at++) {
    for (const c of suggest(ctx, at, suggestOptions)) {
      if (c.at < from || c.at > to) continue;
      if (Number.isFinite(c.score.surprisal) && c.score.surprisal > ceiling) continue;
      if (band !== 'free' && !c.score.functionKept) continue;
      if (c.changesOnsets && c.score.pace > pace) continue;
      const list = byTarget.get(c.at) ?? [];
      if (!list.some((x) => x.id === c.id)) list.push(c);
      byTarget.set(c.at, list);
    }
  }

  const targets = [...byTarget.keys()].sort((a, b) => a - b);
  const out: ReharmCandidate[] = [];
  const salt = variation * GATE_SALT;
  targets.forEach((at, k) => {
    // `spread` gates each eligible chord at the requested density, evenly
    // along the passage; a golden-ratio fraction of the same index picks
    // among that chord's candidates so a passage is not all one move. That
    // fraction is the gate's own, and the gate passes a chord only when it
    // falls below the density — read as it stands, a half-density passage
    // would pick from the bottom half of the range every time (always the
    // top rung, never the back half of a rung's pool). Rescaled over the
    // chords that pass, it spreads evenly again.
    if (!spread(k, density, salt)) return;
    const list = byTarget.get(at)!;
    const gate = frac((k + 1) * PHI + salt);
    const pick = frac((density >= 1 ? gate : gate / density) + variation * 0.37);
    // WHICH RUNG. "Up to Modern" has to sound modern, or the dial reads as
    // doing nothing: taking the simplest qualifying rung everywhere meant a
    // ceiling of 7 produced almost the same colour changes as a ceiling of 0,
    // since a rung-0 candidate qualifies almost everywhere. So the derivation
    // aims AT the ceiling and mixes down from it — the two highest rungs this
    // chord offers, chosen by the same golden-ratio fraction — which is also
    // how an arranger works: a passage sits at a level, with simpler moves
    // among the strong ones rather than instead of them. What is left of the
    // fraction once the rung is chosen picks inside it, so a top-rung pick
    // can reach the back of its pool as well as the front.
    const rungs = [...new Set(list.map((c) => c.rung))].sort((a, b) => b - a);
    const span = Math.min(2, rungs.length);
    const r = Math.floor(pick * span);
    const pool = list.filter((c) => c.rung === rungs[r]);
    out.push(pool[Math.floor((pick * span - r) * pool.length)]);
  });
  return out;
}

/**
 * Apply a derivation as ONE new layer named `name` (default "Reharm N"),
 * derived from whatever is active now. Candidates are applied in order;
 * each is one `replaceChord`, so ids stay valid throughout. Null when nothing
 * was derived. `preview` marks the layer as an audition — applied so it can
 * be heard and seen, discarded unless it is kept.
 */
export function applyDerivation(
  score: Score,
  candidates: readonly ReharmCandidate[],
  name?: string,
  options: { preview?: boolean; derivation?: ReharmDerivation } = {},
): { score: Score; layerId: string } | null {
  if (candidates.length === 0) return null;
  const created = createReharmLayer(score, {
    name,
    from: score.reharm?.active ?? null,
    preview: options.preview,
    derivation: options.derivation,
  });
  let next = created.score;
  for (const c of candidates) {
    const applied = formEdit.replaceChord(next, c.chordId, c.chords, {
      moveId: c.moveId,
      rationale: c.rationale,
    });
    if (applied) next = applied;
  }
  return { score: next, layerId: created.layerId };
}

/** The band a whole derivation sits in — the worst of its candidates. */
export function derivationBand(candidates: readonly ReharmCandidate[]): AdventureBand {
  let worst = 0;
  for (const c of candidates) {
    const band = ADVENTURE_BANDS.indexOf(adventureBandOf(c.score.surprisal));
    if (band > worst) worst = band;
  }
  return ADVENTURE_BANDS[worst];
}
